import React, { createContext, useContext, useState, useEffect } from 'react';
import { requestHelpers } from '../services/api';
import { hashString } from '../utils/security';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);

  // Initialize admin auth state
  useEffect(() => {
    initializeAdminAuth();
  }, []);

  const initializeAdminAuth = async () => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      const adminData = localStorage.getItem('admin');

      if (adminToken && adminData) {
        try {
          const parsedAdmin = JSON.parse(adminData);
          setAdmin(parsedAdmin);
          setIsAdminAuthenticated(true);

          // Optionally fetch fresh admin data or validate token
          // await refreshAdminProfile();
        } catch (error) {
          console.error('Admin token validation failed:', error);
          clearAdminAuthData();
        }
      }
    } catch (error) {
      console.error('Admin auth initialization error:', error);
      clearAdminAuthData();
    } finally {
      setLoading(false);
    }
  };

  const clearAdminAuthData = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin');
    setAdmin(null);
    setIsAdminAuthenticated(false);
    setDashboardStats(null);
  };

  const saveAdminAuthData = (token, adminData) => {
    localStorage.setItem('adminToken', token);
    localStorage.setItem('admin', JSON.stringify(adminData));
    setAdmin(adminData);
    setIsAdminAuthenticated(true);
  };

  // Admin login
  const adminLogin = async (email, password) => {
    try {
      setLoading(true);
      const hashedPassword = await hashString(password);
      const response = await requestHelpers.post('/admin/login', { email, password: hashedPassword });

      if (response.success && response.token) {
        saveAdminAuthData(response.token, response.admin);
      }

      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Admin logout
  const adminLogout = async () => {
    try {
      // Call logout endpoint if needed
      // await requestHelpers.post('/admin/logout');
    } catch (error) {
      console.error('Admin logout API error:', error);
    } finally {
      clearAdminAuthData();
    }
  };

  // Get dashboard statistics
  const getDashboardStats = async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `/admin/dashboard-stats${queryString ? `?${queryString}` : ''}`;
      const response = await requestHelpers.get(url);

      if (response.success) {
        setDashboardStats(response.stats);
      }

      return response;
    } catch (error) {
      throw error;
    }
  };

  // Get all global platform transactions
  const getAllTransactions = async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `/admin/transactions${queryString ? `?${queryString}` : ''}`;
      const response = await requestHelpers.get(url);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Get all users with pagination and filters
  const getUsers = async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `/admin/users${queryString ? `?${queryString}` : ''}`;

      const response = await requestHelpers.get(url);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Get user details by ID
  const getUserDetails = async (userId, params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `/admin/users/${userId}${queryString ? `?${queryString}` : ''}`;
      const response = await requestHelpers.get(url);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Update user wallet balance
  const updateUserWallet = async (userId, amount, action, reason = '') => {
    try {
      const response = await requestHelpers.put(`/admin/users/${userId}/wallet`, {
        amount,
        action,
        reason
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Update user enabled features
  const updateUserFeatures = async (userId, features, flowLimit) => {
    try {
      const response = await requestHelpers.put(`/admin/users/${userId}/features`, {
        features,
        flowLimit
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Update user status
  const updateUserStatus = async (userId, status, reason = '') => {
    try {
      const response = await requestHelpers.put(`/admin/users/${userId}/status`, {
        status,
        reason
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Update user pricing
  const updateUserPricing = async (userId, pricing) => {
    try {
      const response = await requestHelpers.put(`/admin/users/${userId}/pricing`, {
        pricing
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Reset user password by admin
  const resetUserPassword = async (userId, newPassword) => {
    try {
      const hashedPassword = await hashString(newPassword);
      const response = await requestHelpers.post(`/admin/users/${userId}/reset-password`, {
        newPassword: hashedPassword
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Update user approval status and handles subscription duration
  const updateUserApproval = async (userId, isApproved, durationDays) => {
    try {
      const response = await requestHelpers.put(`/admin/users/${userId}/approval`, {
        isApproved,
        durationDays
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Impersonate user: Login as user in new tab
  const impersonateUser = async (userId) => {
    try {
      const response = await requestHelpers.post(`/admin/users/${userId}/impersonate`);
      if (response.success && response.token) {
        // Clear existing user token before impersonating to prevent context contamination
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        const impersonationUrl = `${window.location.origin}/impersonate?token=${response.token}&user=${encodeURIComponent(JSON.stringify(response.user))}`;
        
        // Use window.location.replace to prevent back-navigation into an inconsistent state
        window.location.replace(impersonationUrl);
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Update user details (basic info)
  const updateUserDetails = async (userId, userData) => {
    try {
      const response = await requestHelpers.put(`/admin/users/${userId}`, userData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Delete user
  const deleteUser = async (userId, permanent = false, reason = '') => {
    try {
      const response = await requestHelpers.delete(`/admin/users/${userId}`, {
        data: { permanent, reason }
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Check if admin has specific permission
  const hasPermission = (permission) => {
    if (!admin) return false;
    return admin.role === 'super_admin' || admin.permissions?.includes(permission);
  };

  // Check if admin is super admin
  const isSuperAdmin = () => {
    return admin?.role === 'super_admin';
  };

  // Refresh dashboard stats
  const refreshDashboardStats = async () => {
    try {
      await getDashboardStats();
    } catch (error) {
      console.error('Failed to refresh dashboard stats:', error);
    }
  };

  // Admin context value
  const value = {
    // State
    admin,
    loading,
    isAdminAuthenticated,
    dashboardStats,

    // Actions
    adminLogin,
    adminLogout,
    getDashboardStats,
    refreshDashboardStats,

    // User Management
    getUsers,
    getUserDetails,
    updateUserWallet,
    updateUserFeatures,
    updateUserStatus,
    updateUserApproval,
    impersonateUser,
    updateUserPricing,
    resetUserPassword,
    updateUserDetails,
    deleteUser,
    getAllTransactions,

    // Permissions
    hasPermission,
    isSuperAdmin,

    // Utils
    clearAdminAuthData,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};